import React from 'react';
import { Building2, KeyRound, Loader2, Pencil, Plus, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  bsiInternetBankApi,
  type BsiIbProfileSummary,
  type BsiIbStatus,
} from '@/lib/banks/bsi/api';

type Props = {
  status: BsiIbStatus | null;
  onStatusChange: (status: BsiIbStatus) => void;
  onMessage: (msg: { type: 'success' | 'error'; text: string } | null) => void;
  onProfileSwitched?: () => void;
  disabled?: boolean;
};

type FormState = {
  name: string;
  bankName: string;
  portal: 'corporate' | 'personal' | 'custom';
  ownerType: 'company' | 'person';
  ownerName: string;
  baseUrl: string;
  username: string;
  password: string;
  notes: string;
};

const emptyForm = (portal: FormState['portal'] = 'personal'): FormState => ({
  name: '',
  bankName: 'بانک صادرات',
  portal,
  ownerType: portal === 'corporate' ? 'company' : 'person',
  ownerName: '',
  baseUrl: portal === 'corporate' ? 'https://bib.bsi.ir' : 'https://ib.bsi.ir',
  username: '',
  password: '',
  notes: '',
});

const BsiProfileSwitcher: React.FC<Props> = ({
  status,
  onStatusChange,
  onMessage,
  onProfileSwitched,
  disabled,
}) => {
  const [switchingId, setSwitchingId] = React.useState<number | null>(null);
  const [formMode, setFormMode] = React.useState<'closed' | 'create' | 'edit'>('closed');
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(() => emptyForm());
  const [editingHasPassword, setEditingHasPassword] = React.useState(false);

  const profiles: BsiIbProfileSummary[] = status?.profiles ?? [];
  const activeId = status?.profile?.id ?? profiles.find((p) => p.is_active_selection)?.id;

  const patchForm = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const closeForm = () => {
    setFormMode('closed');
    setEditingId(null);
    setForm(emptyForm());
    setEditingHasPassword(false);
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setForm(emptyForm('personal'));
    setEditingHasPassword(false);
  };

  const openEdit = (p: BsiIbProfileSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setFormMode('edit');
    setEditingId(p.id);
    setEditingHasPassword(Boolean(p.has_password));
    setForm({
      name: p.name,
      bankName: p.bank_name || 'بانک صادرات',
      portal: (p.portal as FormState['portal']) || 'custom',
      ownerType: (p.owner_type as FormState['ownerType']) || (p.portal === 'corporate' ? 'company' : 'person'),
      ownerName: p.owner_name || '',
      baseUrl: p.base_url,
      username: p.default_username || '',
      password: '',
      notes: p.notes || '',
    });
  };

  const handleActivate = async (id: number) => {
    if (id === activeId || switchingId != null) return;
    setSwitchingId(id);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.activateProfile(id);
      onStatusChange(res.data.status);
      onMessage({ type: 'success', text: res.message || 'پروفایل عوض شد.' });
      onProfileSwitched?.();
    } catch (err) {
      onMessage({ type: 'error', text: err instanceof Error ? err.message : 'تعویض پروفایل ناموفق بود.' });
    } finally {
      setSwitchingId(null);
    }
  };

  const refreshStatus = async () => {
    const statusRes = await bsiInternetBankApi.status();
    onStatusChange(statusRes.data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.baseUrl.trim()) return;
    setSaving(true);
    onMessage(null);
    try {
      if (formMode === 'create') {
        const res = await bsiInternetBankApi.createProfile({
          name: form.name.trim(),
          bank_code: 'BSI',
          bank_name: form.bankName.trim() || 'بانک صادرات',
          portal: form.portal,
          owner_type: form.ownerType,
          owner_name: form.ownerName.trim() || undefined,
          base_url: form.baseUrl.trim(),
          default_username: form.username.trim() || undefined,
          password: form.password || undefined,
          notes: form.notes.trim() || undefined,
        });
        await refreshStatus();
        closeForm();
        onMessage({ type: 'success', text: res.message || 'پروفایل ذخیره شد.' });
        if (res.data.id) {
          await handleActivate(res.data.id);
        }
      } else if (formMode === 'edit' && editingId != null) {
        const res = await bsiInternetBankApi.updateProfile(editingId, {
          name: form.name.trim(),
          bank_code: 'BSI',
          bank_name: form.bankName.trim() || 'بانک صادرات',
          portal: form.portal,
          owner_type: form.ownerType,
          owner_name: form.ownerName.trim() || null,
          base_url: form.baseUrl.trim(),
          default_username: form.username.trim() || null,
          password: form.password || undefined,
          notes: form.notes.trim() || null,
        });
        await refreshStatus();
        closeForm();
        onMessage({ type: 'success', text: res.message || 'پروفایل به‌روز شد.' });
      }
    } catch (err) {
      onMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'ذخیره پروفایل ناموفق بود.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">حساب‌های اینترنت‌بانک</div>
          <p className="text-xs text-slate-500">
            هر حساب (حقوقی / حقیقی) با نام کاربری، رمز و آدرس سایت در دیتابیس ذخیره می‌شود. ورود دو مرحله‌ای (OTP)
            همچنان هنگام ورود انجام می‌شود.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1"
          disabled={disabled || switchingId != null}
          onClick={() => (formMode === 'closed' ? openCreate() : closeForm())}
        >
          <Plus className="h-3.5 w-3.5" />
          {formMode === 'closed' ? 'حساب جدید' : 'بستن فرم'}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {profiles.map((p) => {
          const selected = p.id === activeId;
          const Icon = p.portal === 'personal' || p.owner_type === 'person' ? User : Building2;
          return (
            <div
              key={p.id}
              className={cn(
                'rounded-lg border px-3 py-2.5 text-right transition-colors',
                selected
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-slate-50 text-slate-800 hover:border-slate-300 hover:bg-white',
              )}
            >
              <button
                type="button"
                disabled={disabled || switchingId != null}
                onClick={() => void handleActivate(p.id)}
                className="w-full text-right"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
                      <span className="truncate">{p.name}</span>
                    </div>
                    <div className={cn('mt-0.5 text-[11px]', selected ? 'text-slate-300' : 'text-slate-500')}>
                      {p.bank_name || 'بانک صادرات'}
                      <span className="mx-1 opacity-50">·</span>
                      {p.owner_type_label || p.portal_label}
                      {p.owner_name ? (
                        <>
                          <span className="mx-1 opacity-50">·</span>
                          {p.owner_name}
                        </>
                      ) : null}
                    </div>
                    <div className={cn('mt-0.5 text-[11px] font-mono', selected ? 'text-slate-400' : 'text-slate-500')} dir="ltr">
                      {p.base_url.replace(/^https?:\/\//, '')}
                      {p.default_username ? ` · ${p.default_username}` : ''}
                    </div>
                  </div>
                  {switchingId === p.id ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <span
                      className={cn(
                        'mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                        p.session_valid
                          ? selected
                            ? 'bg-emerald-500/30 text-emerald-100'
                            : 'bg-emerald-100 text-emerald-800'
                          : selected
                            ? 'bg-white/15 text-slate-200'
                            : 'bg-slate-200 text-slate-600',
                      )}
                    >
                      {p.session_valid ? 'وارد شده' : 'بدون سشن'}
                    </span>
                  )}
                </div>
              </button>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-[10px]',
                    selected ? 'text-slate-300' : 'text-slate-500',
                  )}
                >
                  <KeyRound className="h-3 w-3" />
                  {p.has_password ? 'رمز ذخیره شده' : 'بدون رمز ذخیره‌شده'}
                </span>
                <Button
                  type="button"
                  variant={selected ? 'secondary' : 'ghost'}
                  size="sm"
                  className={cn('h-7 px-2 text-[11px]', selected && 'bg-white/15 text-white hover:bg-white/25')}
                  disabled={disabled}
                  onClick={(ev) => openEdit(p, ev)}
                >
                  <Pencil className="ml-1 h-3 w-3" />
                  ویرایش
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {formMode !== 'closed' ? (
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 sm:grid-cols-2"
        >
          <div className="space-y-1.5 sm:col-span-2">
            <Label>عنوان حساب</Label>
            <Input
              value={form.name}
              onChange={(e) => patchForm({ name: e.target.value })}
              placeholder="مثلاً صادرات حقوقی — شرکت آداک"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>بانک</Label>
            <Input value={form.bankName} onChange={(e) => patchForm({ bankName: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>نوع پورتال</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.portal}
              onChange={(e) => {
                const next = e.target.value as FormState['portal'];
                patchForm({
                  portal: next,
                  ownerType: next === 'corporate' ? 'company' : next === 'personal' ? 'person' : form.ownerType,
                  baseUrl:
                    next === 'corporate'
                      ? 'https://bib.bsi.ir'
                      : next === 'personal'
                        ? 'https://ib.bsi.ir'
                        : form.baseUrl,
                });
              }}
            >
              <option value="corporate">حقوقی</option>
              <option value="personal">حقیقی</option>
              <option value="custom">سفارشی</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>شرکت / شخص</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.ownerType}
              onChange={(e) => patchForm({ ownerType: e.target.value as FormState['ownerType'] })}
            >
              <option value="company">شرکت</option>
              <option value="person">شخص</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>به نام کیست</Label>
            <Input
              value={form.ownerName}
              onChange={(e) => patchForm({ ownerName: e.target.value })}
              placeholder="نام شرکت یا شخص"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>آدرس سایت</Label>
            <Input
              dir="ltr"
              className="font-mono text-sm"
              value={form.baseUrl}
              onChange={(e) => patchForm({ baseUrl: e.target.value })}
              placeholder="https://bib.bsi.ir"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>نام کاربری</Label>
            <Input
              value={form.username}
              onChange={(e) => patchForm({ username: e.target.value })}
              autoComplete="username"
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              رمز عبور
              {formMode === 'edit' && editingHasPassword ? (
                <span className="mr-1 text-[11px] font-normal text-slate-500">(خالی = بدون تغییر)</span>
              ) : null}
            </Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => patchForm({ password: e.target.value })}
              autoComplete="new-password"
              placeholder={formMode === 'edit' && editingHasPassword ? 'رمز جدید (اختیاری)' : 'رمز ورود بانک'}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>یادداشت (اختیاری)</Label>
            <Input value={form.notes} onChange={(e) => patchForm({ notes: e.target.value })} />
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? <Loader2 className="ml-1 h-3.5 w-3.5 animate-spin" /> : null}
              {formMode === 'edit' ? 'به‌روزرسانی' : 'ذخیره در دیتابیس'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={closeForm}>
              انصراف
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default BsiProfileSwitcher;
